import { X, Coins, Sparkles, Loader2, History, ShoppingBag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useStyleCredits, CreditPackage } from '@/hooks/useStyleCredits';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { TransactionHistory } from '@/components/credits/TransactionHistory';

interface CreditShopModalProps {
  onClose: () => void;
  initialTab?: 'shop' | 'history';
}

function PackageCard({ 
  pkg, 
  isPopular,
  isPurchasing,
  onPurchase 
}: { 
  pkg: CreditPackage;
  isPopular: boolean;
  isPurchasing: boolean;
  onPurchase: () => void;
}) {
  const totalCredits = pkg.credits + pkg.bonus_credits;
  const priceInRupees = pkg.price_inr / 100;
  const costPerCredit = (priceInRupees / totalCredits).toFixed(3);

  return (
    <div className={cn(
      'relative p-4 rounded-xl border transition-all',
      'bg-card hover:border-primary/50',
      isPopular ? 'border-primary ring-1 ring-primary/20' : 'border-border'
    )}>
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground rounded-full">
            Most Popular
          </span>
        </div>
      )}

      <div className="text-center mb-3">
        <h3 className="font-semibold text-base text-foreground">{pkg.name}</h3>
        <div className="flex items-baseline justify-center gap-1 mt-1">
          <span className="text-xl font-bold text-foreground">₹{priceInRupees}</span>
        </div>
      </div>

      <div className="space-y-1.5 mb-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Base Credits</span>
          <span className="font-medium text-foreground">{pkg.credits.toLocaleString()}</span>
        </div>
        {pkg.bonus_credits > 0 && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-primary flex items-center gap-1">
              <Sparkles size={10} />
              Bonus
            </span>
            <span className="font-medium text-primary">
              +{pkg.bonus_credits.toLocaleString()}
            </span>
          </div>
        )}
        <div className="h-px bg-border" />
        <div className="flex items-center justify-between text-xs">
          <span className="font-medium text-foreground">Total Credits</span>
          <span className="font-bold text-foreground">{totalCredits.toLocaleString()}</span>
        </div>
        <div className="text-[9px] text-center text-muted-foreground">
          ₹{costPerCredit} per credit
        </div>
      </div>

      <Button
        onClick={onPurchase}
        disabled={isPurchasing}
        size="sm"
        className={cn(
          'w-full text-xs',
          isPopular 
            ? 'bg-primary hover:bg-primary/90 text-primary-foreground' 
            : 'bg-secondary hover:bg-secondary/80'
        )}
      >
        {isPurchasing ? (
          <Loader2 size={14} className="animate-spin mr-1.5" />
        ) : (
          <Coins size={14} className="mr-1.5" />
        )}
        {isPurchasing ? 'Processing...' : 'Buy Now'}
      </Button>
    </div>
  );
}

export function CreditShopModal({ onClose, initialTab = 'shop' }: CreditShopModalProps) {
  const { balance, packages, purchaseCredits, CREDIT_COSTS } = useStyleCredits();
  const [purchasingId, setPurchasingId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'shop' | 'history'>(initialTab);

  const handlePurchase = async (packageId: string) => {
    setPurchasingId(packageId);
    try {
      const success = await purchaseCredits(packageId);
      if (success) {
        onClose();
      }
    } finally {
      setPurchasingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-background/80 backdrop-blur-sm">
      <div className="w-full sm:max-w-lg max-h-[90vh] overflow-hidden rounded-t-2xl sm:rounded-2xl shadow-2xl bg-card border border-border flex flex-col">
        {/* Header - Fixed */}
        <div className="flex-shrink-0 p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                <Coins size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground">Style Credits</h2>
                <p className="text-xs text-muted-foreground">
                  Balance: <span className="font-semibold text-primary">{balance.toLocaleString()}</span>
                </p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('shop')}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all',
                activeTab === 'shop'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              )}
            >
              <ShoppingBag size={16} />
              Buy Credits
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all',
                activeTab === 'history'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground'
              )}
            >
              <History size={16} />
              History
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'shop' ? (
            <>
              {/* Packages Grid - Mobile First */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                {packages.map((pkg) => (
                  <PackageCard
                    key={pkg.id}
                    pkg={pkg}
                    isPopular={pkg.name === 'Popular'}
                    isPurchasing={purchasingId === pkg.id}
                    onPurchase={() => handlePurchase(pkg.id)}
                  />
                ))}
              </div>

              {/* Credit Costs Reference - Compact */}
              <div className="p-3 rounded-xl bg-muted/50">
                <h4 className="text-xs font-medium text-foreground mb-2">Credit Costs</h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Outfit Gen</span>
                    <span className="font-medium text-foreground">{CREDIT_COSTS.outfit_generation}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">OOTD Rating</span>
                    <span className="font-medium text-foreground">{CREDIT_COSTS.ootd_rating}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Try-On</span>
                    <span className="font-medium text-foreground">{CREDIT_COSTS.virtual_tryon}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">AI Chat</span>
                    <span className="font-medium text-foreground">{CREDIT_COSTS.agent_chat}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Analysis</span>
                    <span className="font-medium text-foreground">{CREDIT_COSTS.clothing_analysis}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Packing</span>
                    <span className="font-medium text-foreground">{CREDIT_COSTS.packing_list}</span>
                  </div>
                </div>
              </div>

              <p className="text-[9px] text-center text-muted-foreground mt-3">
                Secure payment via Razorpay • UPI, Cards, Wallets accepted
              </p>
            </>
          ) : (
            <TransactionHistory />
          )}
        </div>
      </div>
    </div>
  );
}
